import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ContractChangeStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { deleteAgreement } from '../AgreementsQueries';

export const useDeleteAgreementItem = ({
    refreshDataHandler,
    refreshProject,
    isLastItem,
}: {
    refreshDataHandler: () => void;
    refreshProject: () => void;
    isLastItem: boolean;
}) => {
    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const dispatch = useDispatch();
    const [confirmationDialogOpened, setConfirmationDialogOpened] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [hasError, setHasError] = useState(false);
    const [currentIdForDelete, setCurrentIdForDelete] = useState<number | null>(null);

    const openConfirmationDialogHandler = useCallback(
        ({ id, status }: { id: number; status: ContractChangeStatusEnum | undefined }) => {
            setCurrentIdForDelete(id);
            setConfirmationDialogOpened(true);

            sendGpnAnalyticsEvent({
                componentId: 'rim_delete DS button_pressed',
                projectId,
                contractId: Number(projectData?.contractId),
                agreementId: id,
                agreementStatus: status,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: projectData?.methodology,
            });
        },
        [
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
        ],
    );

    const [deleteAgreementData, deleteAgreementStatus, deleteAgreementError, deleteAgreementRequest] = useRequest({
        requestId: 'deleteAgreement',
        query: deleteAgreement,
        withEffect: false,
    });

    const [resetDeleteData] = useResetStoreData({ requestId: 'deleteAgreement' });

    const submitDeleteHandler = useCallback(() => {
        setIsSubmitted(true);
        dispatch(deleteAgreementRequest({ contractChangeId: currentIdForDelete }));
    }, [currentIdForDelete, deleteAgreementRequest, dispatch]);

    const cancelDeleteHandler = useCallback(() => {
        setConfirmationDialogOpened(false);
    }, []);

    const closeErrorModalHandler = useCallback(() => {
        setHasError(false);
        resetDeleteData(null);
    }, [resetDeleteData]);

    const createErrorReportHandler = useCallback(() => {
        ExportCSVReport(deleteAgreementData?.uerDeleteContractChange?.validation);
    }, [deleteAgreementData?.uerDeleteContractChange?.validation]);

    useEffect(() => {
        if (isSubmitted && deleteAgreementData?.uerDeleteContractChange?.result) {
            setIsSubmitted(false);
            if (isLastItem) {
                refreshProject?.();
                refreshDataHandler?.();
            } else {
                refreshDataHandler?.();
            }
            resetDeleteData(null);
        }
        if (
            isSubmitted &&
            (deleteAgreementData?.uerDeleteContractChange?.message ||
                deleteAgreementData?.uerDeleteContractChange?.validation?.length)
        ) {
            setHasError(true);
        }
    }, [
        deleteAgreementData?.uerDeleteContractChange?.message,
        deleteAgreementData?.uerDeleteContractChange?.result,
        deleteAgreementData?.uerDeleteContractChange?.validation?.length,
        isLastItem,
        isSubmitted,
        refreshDataHandler,
        refreshProject,
        resetDeleteData,
    ]);

    return {
        confirmationDialogOpened,
        openConfirmationDialogHandler,
        submitDeleteHandler,
        cancelDeleteHandler,
        hasError,
        deleteAgreementData,
        deleteAgreementStatus,
        deleteAgreementError,
        closeErrorModalHandler,
        createErrorReportHandler,
    };
};
